// Shared visual identity for the DLC modules (Projekty ZE / Obieg dokumentów).
// Used by the Sidebar section badges and the dashboard "Moduły DLC" panel so the
// glossy look + gradients + labels stay in a single place.

/* Glossy "glass" sheen shared by the DLC badges. Three layers:
   - ring + drop shadow for depth,
   - `before:` static top highlight (the glass curve),
   - `after:` animated light streak that sweeps across (the "shine"/glint).
   Combine with a `bg-gradient-to-r from-…/to-…` color and `text-white`. */
export const DLC_BADGE_GLOSS =
  "relative overflow-hidden ring-1 ring-inset ring-white/30 shadow-md " +
  "before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-1/2 " +
  "before:bg-gradient-to-b before:from-white/55 before:to-transparent " +
  "after:pointer-events-none after:absolute after:inset-0 after:content-[''] " +
  "after:bg-[linear-gradient(120deg,transparent_40%,rgba(255,255,255,0.4)_50%,transparent_60%)] " +
  "after:animate-dlc-shine motion-reduce:after:hidden";

/* Base shape/typography of the small DLC code chip ("DLC_01"). Combine with
   DLC_BADGE_GLOSS and a module gradient. Shared by the sidebar and dashboard. */
export const DLC_BADGE_CHIP =
  "text-[9px] font-mono font-bold rounded px-1.5 py-0.5 leading-none uppercase tracking-wider shadow-sm";

export interface DlcModuleMeta {
  /** Value stored in User.accessible_dlc_modules. */
  id: string;
  /** Short code shown on the badge chip. */
  badge: string;
  /** Human-readable Polish label. */
  label: string;
  /** Tailwind gradient classes for the chip background. */
  gradient: string;
  /** Route to the module's landing page (used by the dashboard tiles). */
  to: string;
  /** Secondary scope folded into this module. Rendered on the dashboard tile as
      an extra badge chip next to the main code plus a "+ …" subtitle line, so a
      module that grew to cover another DLC slot reads at a glance. */
  extension?: {
    /** Short code shown on the secondary chip, e.g. "DLC_05". */
    badge: string;
    /** Human-readable Polish description of the folded-in scope. */
    label: string;
  };
}

/** All DLC modules, in display order. */
export const DLC_MODULES: DlcModuleMeta[] = [
  {
    id: "dlc_01",
    badge: "DLC_01",
    label: "Projekty ZE",
    gradient: "bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white",
    to: "/power-projects",
    extension: {
      badge: "DLC_05",
      label: "Analityka AI przetargów ZE",
    },
  },
  {
    id: "dlc_02",
    badge: "DLC_02",
    label: "Obieg dokumentów",
    gradient: "bg-gradient-to-r from-emerald-500 to-teal-500 text-white",
    to: "/paper-send/mine",
  },
  {
    id: "dlc_03",
    badge: "DLC_03",
    label: "ARON — asystent AI",
    gradient: "bg-gradient-to-r from-red-600 via-red-700 to-black text-white",
    to: "/aron",
  },
  {
    id: "dlc_04",
    badge: "DLC_04",
    label: "ENG-ARON — sprawdzanie opracowań",
    gradient: "bg-gradient-to-r from-amber-500 to-orange-600 text-white",
    to: "/eng-aron",
  },
  {
    id: "dlc_06",
    badge: "DLC_06",
    label: "FIN-ARON — finanse projektów ZE",
    gradient: "bg-gradient-to-r from-sky-600 to-cyan-500 text-white",
    to: "/fin-aron",
  },
  {
    id: "dlc_07",
    badge: "DLC_07",
    label: "EZ-ARON — analiza przetargów BZP",
    gradient: "bg-gradient-to-r from-pink-500 to-rose-600 text-white",
    to: "/ezamowienia",
  },
  {
    id: "dlc_10",
    badge: "DLC_10",
    label: "Obliczenia techniczne",
    gradient: "bg-gradient-to-r from-lime-500 to-emerald-600 text-white",
    to: "/tech-calc",
  },
];

/** Minimalny dostęp do DLC_01 — sam zbiorczy widok działek. Kafelek pokazywany tylko
    wtedy, gdy nie ma pełnego DLC_01 (pełny moduł go zawiera). */
const DLC_01_MINIMAL: DlcModuleMeta = {
  id: "dlc_01_min",
  badge: "DLC_01",
  label: "Projekty ZE — działki",
  gradient: "bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white",
  to: "/power-projects/dzialki",
};

/** DLC modules the given access list grants, in display order. */
export function accessibleDlcModules(
  accessible: string[] | undefined,
): DlcModuleMeta[] {
  const granted = accessible ?? [];
  const modules = DLC_MODULES.filter((m) => granted.includes(m.id));
  if (!granted.includes("dlc_01") && granted.includes("dlc_01_min")) {
    modules.unshift(DLC_01_MINIMAL);
  }
  return modules;
}

/** True when the user reaches DLC_01 only through the minimal grant (parcels view) —
    links into projects and parcel details must not be offered then. */
export function hasOnlyMinimalDlc01(accessible: string[] | undefined): boolean {
  const granted = accessible ?? [];
  return !granted.includes("dlc_01") && granted.includes("dlc_01_min");
}
